import Lightbox from "@/src/components/profile/light-box";
import PhotoGrid from "../../profile/photo-grid";

import ScButton from "@/src/components/ui-kit/button";
import { useAuth } from "@/src/contexts/auth.context";
import { useUploadFile } from "@/src/hooks/features/use-upload-file";
import { UserPhoto } from "@/src/models/user.model";
import {
  useDeleteEventGalleryMutation,
  useGetEventGalleryQuery,
  useInsertEventGalleryMutation,
} from "@/src/services/api/events/events.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import { pickMedia } from "@/src/utils/picker-utils";
import { ImagePickerAsset } from "expo-image-picker";
import { CloseCircle, DocumentUpload } from "iconsax-react-nativejs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Image, Pressable, ScrollView, Text, View } from "react-native";
import ConfirmModal from "../../action-modals/confirm-modal";
import { SectionCard } from "./event-detail-section-card";

type EventDetailPhotosProps = Readonly<{
  eventId: string;
  isOrganizer: boolean;
}>;

const EventDetailPhotos = ({
  eventId,
  isOrganizer,
}: EventDetailPhotosProps) => {
  const { themeVars } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const [pendingAssets, setPendingAssets] = useState<ImagePickerAsset[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState<UserPhoto | null>(null);

  const { data: photos = [] } = useGetEventGalleryQuery(eventId);
  const { mutateAsync: insertPhoto, isPending: isInserting } =
    useInsertEventGalleryMutation();
  const { mutateAsync: deletePhoto } = useDeleteEventGalleryMutation();

  const { mutateAsync: uploadFile } = useUploadFile();
  const mappedImages = photos.map((photo) => ({
    id: photo.ItemId,
    url: photo.imageUrl,
    file_name: photo.ItemId,
    file_size: 0,
    mime_type: "image/jpeg",
    createdBy: photo.CreatedBy,
  }));

  const handlePickFromGallery = async () => {
    const assets = await pickMedia(false, "images");
    if (!assets) return;
    setPendingAssets((prev) => [...prev, ...assets]);
  };

  const handleConfirmUpload = async () => {
    setIsUploading(true);
    try {
      for (const asset of pendingAssets) {
        const imageUrl = await uploadFile({
          uri: asset.uri,
          fileName: asset.fileName ?? `gallery_${Date.now()}.jpg`,
          mimeType: asset.mimeType ?? "image/jpeg",
        });
        await insertPhoto({ eventId, imageUrl });
      }
      setPendingAssets([]);
    } finally {
      setIsUploading(false);
    }
  };
  const handleDeletePhotoRequest = (photo: UserPhoto) => {
    setPhotoToDelete(photo);
  };

  const handleDeleteConfirm = async () => {
    if (!photoToDelete) return;
    await deletePhoto({ itemId: photoToDelete.id });
    setPhotoToDelete(null);
  };
  const handleRemovePending = (index: number) => {
    setPendingAssets((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCancelUpload = () => setPendingAssets([]);

  return (
    <>
      <SectionCard>
        <View className="flex-row items-center justify-between mb-3">
          <Text
            className="text-base font-semibold"
            style={{ color: themeVars["--color-onyx-default"] as string }}
          >
            {t("LABEL_PHOTOS")}
          </Text>
          <ScButton
            text="BTN_SELECT"
            onPress={handlePickFromGallery}
            loading={isInserting}
            mode="outlined"
            icon={<DocumentUpload size={16} color={themeVars["--color-primary-default"] as string} />}
          />
        </View>

        {pendingAssets.length > 0 && (
          <View className="gap-3 mb-3">
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <View className="flex-row gap-2">
                {pendingAssets.map((asset, index) => (
                  <View
                    key={index}
                    className="  w-20 h-20 rounded-xl overflow-hidden bg-ash"
                  >
                    <Image
                      source={{ uri: asset.uri }}
                      className="relative w-full h-full"
                      resizeMode="cover"
                    />
                    <Pressable
                      onPress={() => handleRemovePending(index)}
                      hitSlop={8}
                      className="absolute top-1 right-1 bg-black/50 rounded-full active:opacity-70 hover:bg-ash-3"
                    >
                      <CloseCircle size={16} color="#fff" />
                    </Pressable>
                  </View>
                ))}
              </View>
            </ScrollView>
            <View className="flex-row gap-2">
              <ScButton
                text="BTN_CANCEL"
                mode="outlined"
                onPress={handleCancelUpload}
                className="flex-1"
              />
              <ScButton
                text="BTN_UPLOAD"
                onPress={handleConfirmUpload}
                loading={isUploading}
                className="flex-1"
              />
            </View>
          </View>
        )}
        {mappedImages.length === 0 ? (
          <Text className="text-sm text-center py-4 text-slate">
            {t("LABEL_NO_PHOTOS_UPLOADED")}
          </Text>
        ) : (
          <PhotoGrid photos={mappedImages} onPress={setLightboxIndex} />
        )}

        {lightboxIndex !== null && (
          <Lightbox
            photos={mappedImages}
            index={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
            onDelete={
              isOrganizer ||
              mappedImages[lightboxIndex]?.createdBy === user?.itemId
                ? handleDeletePhotoRequest
                : undefined
            }
          >
            <ConfirmModal
              visible={!!photoToDelete}
              title="LABEL_DELETE_PHOTO_CONFIRM"
              description="HINT_DELETE_PHOTO_CONFIRM"
              confirmText="BTN_DELETE"
              cancelText="BTN_CANCEL"
              onCancel={() => setPhotoToDelete(null)}
              onConfirm={handleDeleteConfirm}
            />
          </Lightbox>
        )}
      </SectionCard>
    </>
  );
};

export default EventDetailPhotos;
